import React, { useEffect, useState } from 'react';
import semver from 'semver';
import { AxiosError } from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GENERAL_FONT, MHColours } from '../../constants/theme';
import FlashSnackbar, { Severity } from '../common/FlashSnackbar';
import {
  getAppVersion,
  updateAppVersion,
  AppVersions,
} from '../../lib/services/appVersionService';
import { reportError } from '../../lib/sentry';

const SEMVER_RE = /^\d+\.\d+\.\d+$/;

interface PlatformSectionProps {
  label: string;
  current: string | undefined;
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  saving: boolean;
  disabled: boolean;
}

const PlatformSection: React.FC<PlatformSectionProps> = ({
  label,
  current,
  value,
  onChange,
  onSave,
  saving,
  disabled,
}) => {
  const { t } = useTranslation();
  const isValid = SEMVER_RE.test(value);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography
          style={{
            fontFamily: GENERAL_FONT,
            fontWeight: 'bold',
            marginBottom: 4,
          }}
        >
          {label}
        </Typography>
        <Typography
          style={{
            fontFamily: GENERAL_FONT,
            fontSize: 13,
            color: MHColours.mhGrayDark,
            marginBottom: 12,
          }}
        >
          {t('appVersion.current')}: {current ?? t('appVersion.notSet')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            label={t('appVersion.minimumVersion')}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={value !== '' && !isValid}
            helperText={
              value !== '' && !isValid ? t('appVersion.invalidSemver') : ''
            }
            sx={{ width: 160 }}
            disabled={disabled}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={disabled || !current || !SEMVER_RE.test(current)}
            onClick={() => onChange(semver.inc(current!, 'minor')!)}
            sx={{ textTransform: 'none', height: 40 }}
          >
            +minor
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled || !current || !SEMVER_RE.test(current)}
            onClick={() => onChange(semver.inc(current!, 'patch')!)}
            sx={{ textTransform: 'none', height: 40 }}
          >
            +patch
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={disabled || saving || !isValid}
            onClick={onSave}
            sx={{
              textTransform: 'none',
              height: 40,
              backgroundColor: MHColours.mhGreen,
              '&:hover': { backgroundColor: MHColours.mhGreenDark },
            }}
          >
            {t('appVersion.update')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const AppVersionManager: React.FC = () => {
  const { t } = useTranslation();

  const [current, setCurrent] = useState<AppVersions | null>(null);
  const [iosInput, setIosInput] = useState('');
  const [androidInput, setAndroidInput] = useState('');
  const [force, setForce] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState<{
    open: boolean;
    msg: string;
    severity: Severity;
  }>({
    open: false,
    msg: '',
    severity: 'success',
  });

  useEffect(() => {
    getAppVersion()
      .then(({ data }) => {
        const v = data.result.appVersion;
        setCurrent(v);
        setIosInput(v?.ios ?? '');
        setAndroidInput(v?.android ?? '');
      })
      .catch((err) => {
        reportError(err, { tags: { area: 'app_version' } });
        setFlash({
          open: true,
          msg: t('appVersion.loadError'),
          severity: 'error',
        });
      });
  }, [t]);

  const handleSave = async (platform: 'ios' | 'android') => {
    setSaving(true);
    const fields: AppVersions & { force?: boolean } =
      platform === 'ios'
        ? { ios: iosInput, force: force || undefined }
        : { android: androidInput, force: force || undefined };

    try {
      const { data } = await updateAppVersion(fields);
      const updated = data.result.appVersion;
      setCurrent(updated);
      setFlash({
        open: true,
        msg: t('appVersion.updateSuccess'),
        severity: 'success',
      });
    } catch (err) {
      reportError(err, { tags: { area: 'app_version' } });
      const msg =
        (err as AxiosError<{ message?: string }>)?.response?.data?.message ??
        t('appVersion.updateError');
      setFlash({ open: true, msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600 }}>
      <Typography
        style={{
          fontSize: 20,
          fontWeight: 'bold',
          fontFamily: GENERAL_FONT,
          marginBottom: 8,
        }}
      >
        {t('appVersion.title')}
      </Typography>
      <Typography
        style={{
          fontFamily: GENERAL_FONT,
          fontSize: 13,
          color: MHColours.mhGrayDark,
          marginBottom: 20,
        }}
      >
        {t('appVersion.description')}
      </Typography>

      <PlatformSection
        label="iOS"
        current={current?.ios}
        value={iosInput}
        onChange={setIosInput}
        onSave={() => handleSave('ios')}
        saving={saving}
        disabled={saving}
      />

      <PlatformSection
        label="Android"
        current={current?.android}
        value={androidInput}
        onChange={setAndroidInput}
        onSave={() => handleSave('android')}
        saving={saving}
        disabled={saving}
      />

      <FormControlLabel
        sx={{ alignItems: 'flex-start', mt: 1 }}
        control={
          <Checkbox
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            size="small"
            sx={{ pt: 0 }}
          />
        }
        label={
          <Box>
            <Typography
              style={{
                fontFamily: GENERAL_FONT,
                fontSize: 13,
                fontWeight: 'bold',
              }}
            >
              {t('appVersion.forceLabel')}
            </Typography>
            <Typography
              style={{
                fontFamily: GENERAL_FONT,
                fontSize: 12,
                color: MHColours.mhGrayDark,
              }}
            >
              {t('appVersion.forceDescription')}
            </Typography>
          </Box>
        }
      />

      <FlashSnackbar
        open={flash.open}
        msg={flash.msg}
        severity={flash.severity}
        onClose={() => setFlash((f) => ({ ...f, open: false }))}
      />
    </Box>
  );
};

export default AppVersionManager;
